import React, { ReactNode, useEffect, useState } from "react";
import { isAuthenticated, isNative } from "@origin-digital/platform-helpers";
import { logger } from "@origin-digital/reporting-client";
import {
  CustomerContext,
  INavigationAPIResponse,
} from "../../shared/interface/navigation";
import {
  ILabelAPIResponse,
  path as articlePath,
} from "../../shared/interface/label";
import { newSessionId } from "./services/session";
import { fetchVisitorToken } from "./services/visitorToken";
import { getQueryParam } from "./helpers/url.helper";
import { IFetch, useDaxi } from "./services/useDaxi";

export interface IAppContext {
  fetchSessionId: () => void;
  navigation: IFetch<INavigationAPIResponse>;
  articleLabels: IFetch<ILabelAPIResponse>;
  session: IFetch<ISessionResponse>;
  visitorToken?: string;
  customerContext?: CustomerContext;
  config: IAppConfig;
  isAuthenticated: boolean;
  isNative: boolean;
}

export interface ISessionResponse {
  sessionId?: string;
  timeout?: number;
}

const CUSTOMER_CONTEXT_PARAM = "context";
const AUTH_CONTEXT = "auth";
const UNAUTH_CONTEXT = "unauth";
const VALID_CUSTOMER_CONTEXTS: CustomerContext[] = [
  AUTH_CONTEXT,
  UNAUTH_CONTEXT,
];
const isValidCustomerContext = (context: CustomerContext): boolean => {
  return VALID_CUSTOMER_CONTEXTS.includes(context);
};

const initialFetch = {
  loading: true,
  error: false,
  data: undefined,
};

export const AppContext = React.createContext<IAppContext>({
  navigation: initialFetch,
  articleLabels: initialFetch,
  session: initialFetch,
  isAuthenticated: false,
  isNative: false,
  config: {} as IAppConfig,
  fetchSessionId: () => {
    logger.debug("[fetchSessionId]: not ready yet!");
  },
});

export const useAppContext = (): IAppContext => React.useContext(AppContext);

export const AppContextConsumer = AppContext.Consumer;

export const AppContextProvider = ({
  children,
  config,
}: {
  children: ReactNode;
  config: IAppConfig;
}): JSX.Element => {
  const [session, setSession] = useState<IFetch<ISessionResponse>>(
    initialFetch
  );

  const [visitorToken, setVisitorToken] = useState<
    IAppContext["visitorToken"]
  >();
  const [customerContext, setCustomerContext] = useState<
    IAppContext["customerContext"]
  >();

  const fetchSessionId = () => {
    //session setup
    newSessionId(
      config.bold360host,
      config.bold360kb,
      customerContext,
      visitorToken
    ).then((res) => setSession({ error: false, loading: false, data: res }));
  };

  //fetch navigation data
  const navigation = useDaxi<INavigationAPIResponse>(
    "/apic/helpsupport/v1/navigation",
    { ttlInMin: config.ttl }
  );

  const articleLabels = useDaxi<ILabelAPIResponse>(articlePath, {
    ttlInMin: config.ttl,
  });

  useEffect(() => {
    //set customer context based on whether the user is authed
    setCustomerContext(isAuthenticated() ? AUTH_CONTEXT : UNAUTH_CONTEXT);

    //set or override customer context from query param
    const context = getQueryParam(CUSTOMER_CONTEXT_PARAM) as CustomerContext;
    if (isValidCustomerContext(context)) {
      setCustomerContext(context);
    }

    //vistor token setup
    fetchVisitorToken(config.bold360host)
      .then(setVisitorToken)
      .then(fetchSessionId);
  }, []);

  useEffect(() => {
    let interval;
    if (session?.data?.sessionId && session?.data?.timeout) {
      interval = setInterval(() => {
        logger.debug("clearing out sessionId");
        setSession({
          ...session,
          data: { sessionId: undefined, timeout: undefined },
        });
      }, session.data.timeout * 1000);
    }

    return () => interval && clearInterval(interval);
  }, [session]);

  return (
    <AppContext.Provider
      value={{
        navigation,
        session,
        visitorToken,
        customerContext,
        config,
        isAuthenticated: isAuthenticated(),
        isNative: isNative(),
        articleLabels,
        fetchSessionId,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
